import React from "react";
import PropTypes from "prop-types";
import TreeRails, { withTreeGuides } from "BSM/components/TreeRails";
import styles from "./FilterItem.module.css";

export function FilterItem({
  title,
  options = [],
  selected = [],
  onChange = () => {},
  allowCustom = false,
  matchMode,
  onMatchModeChange,
  onClear,
}) {
  const [customValue, setCustomValue] = React.useState("");

  // Options may be plain strings or { value, label } objects (so we can show a
  // friendly label like "Customer (C)" while still matching on the code "C").
  // Hierarchical option sets (e.g. the Category tree) additionally carry a
  // 1-based `depth` so children render indented under their parent, and an
  // optional `hint` shown on hover — without those, sibling branches that reuse
  // the same title (four different "Bundles" categories) are indistinguishable.
  const normalizedOptions = options.map((option) =>
    typeof option === "string"
      ? { value: option, label: option, depth: 1, hint: undefined }
      : {
          value: option.value,
          label: option.label ?? option.value,
          depth: option.depth || 1,
          hint: option.hint,
        }
  );

  // Nested option sets get connector rails so it's obvious which parent a row
  // belongs to. Flat groups (every other filter) skip all of this and render
  // exactly as before.
  const isTree = normalizedOptions.some((option) => option.depth > 1);
  const treeOptions = isTree ? withTreeGuides(normalizedOptions) : null;

  const handleToggle = (value) => (event) => {
    onChange(value, event.target.checked);
  };

  const handleCustomSubmit = (event) => {
    event.preventDefault();
    const value = customValue.trim();
    if (!value) return;
    onChange(value, true);
    setCustomValue("");
  };

  const showModeToggle = typeof onMatchModeChange === "function";
  const mode = String(matchMode || "AND").toUpperCase();
  // Clearable groups (e.g. Filter by Tag) always show the Clear button so it's
  // discoverable; it's simply a no-op when nothing is selected.
  const canClear = typeof onClear === "function";
  const matchHint =
    'Choose how multiple selections combine. "Any" shows items that match at ' +
    'least one selection (broader results). "All" shows only items that match ' +
    "every selection (narrower results).";

  return (
    <div className={styles.item}>
      {title && <p className={styles.title}>{title}</p>}
      <ul
        className={`${styles.optionList}${
          isTree ? ` ${styles.optionListTree}` : ""
        }`}
      >
        {(treeOptions || normalizedOptions).map((option) => {
          const checkboxId = `${title || "filter"}-${option.value}`;
          return (
            <li className={styles.optionItem} key={option.value}>
              {isTree && (
                <TreeRails
                  depth={option.depth}
                  ancestorContinues={option.ancestorContinues}
                  isLastChild={option.isLastChild}
                />
              )}
              <label
                className={styles.optionLabel}
                htmlFor={checkboxId}
                title={option.hint}
              >
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={handleToggle(option.value)}
                  aria-label={option.hint || option.label}
                />
                <span>{option.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {canClear && (
        <button
          type="button"
          className={styles.clearBtn}
          onClick={() => onClear()}
          aria-label={`Clear ${title || "filter"}`}
          title={`Clear the selected ${title || "filter"} options.`}
        >
          Clear
        </button>
      )}
      {allowCustom && (
        <form className={styles.customRow} onSubmit={handleCustomSubmit}>
          <input
            type="text"
            className={styles.customInput}
            placeholder="Other tag…"
            value={customValue}
            onChange={(event) => setCustomValue(event.target.value)}
            aria-label={`Filter by another ${title || "value"}`}
          />
          <button
            type="submit"
            className={styles.customAdd}
            disabled={!customValue.trim()}
            aria-label="Apply filter"
            title={`Add this ${title || "value"} to the filter.`}
          >
            +
          </button>
        </form>
      )}
      {showModeToggle && (
        <div
          className={styles.matchMode}
          role="group"
          aria-label={`Match mode for ${title || "filter"}`}
          title={matchHint}
        >
          <span className={styles.matchModeLabel} title={matchHint}>
            Match
          </span>
          <div className={styles.matchModeToggle}>
            <button
              type="button"
              className={`${styles.matchModeBtn} ${
                mode === "OR" ? styles.matchModeBtnActive : ""
              }`}
              onClick={() => onMatchModeChange("OR")}
              aria-pressed={mode === "OR"}
              title={`Any — ${matchHint}`}
            >
              Any
            </button>
            <button
              type="button"
              className={`${styles.matchModeBtn} ${
                mode === "AND" ? styles.matchModeBtnActive : ""
              }`}
              onClick={() => onMatchModeChange("AND")}
              aria-pressed={mode === "AND"}
              title={`All — ${matchHint}`}
            >
              All
            </button>
          </div>
        </div>
      )}
      <div className={styles.divider} />
    </div>
  );
}

FilterItem.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string,
        depth: PropTypes.number,
        hint: PropTypes.string,
      }),
    ])
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  allowCustom: PropTypes.bool,
  matchMode: PropTypes.oneOf(["AND", "OR", "and", "or"]),
  onMatchModeChange: PropTypes.func,
  onClear: PropTypes.func,
};
